import React, { useMemo, useState } from "react";
import { colors2, TabBarV2 } from "oolib";
import { UI_BODY_SEMIBOLD_SM } from "oolib/dist/v2/components/Typo2";

import { PillSwitch } from "@/components/analysisModules/TopicClusteringTreeView/PrioritiesLayout/PillSwitch";
import OverlayScrollbar from "@/components/UI/OverlayScrollbar";
import { WidgetEmptyState } from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { WIDGET_MAX_HEIGHT } from "../../../../../constants";
import { DATASETS, isMadTenant } from "../madSettings";
import { StartedChildren } from "./StartedChildren";
import { NotStartedChildren } from "./NotStartedChildren";
import { AssessmentStatsBar } from "./AssessmentStatsBar";

export { AssessmentStatusConfigForm as ConfigForm } from "./AssessmentStatusConfigForm";

/**
 * MAD - Assessment Completion — which assessments each child has had.
 *
 *   tabs (TabBarV2)   one per assessment template: Numeracy / Literacy / Life Skills,
 *                     on a white band — the dashboard's own arrangement, the
 *                     tab bar white over a grey body
 *   stats bar         "Average Assessment Grade": one card per assessment —
 *                     average assessment grade, and how many children had it
 *                     out of all in MAD's feed
 *   pill switch       "Per Student Assessment Completion Status": Started /
 *                     Not started
 *
 * Started      children with at least one assessment in the template, a tick
 *              or cross per assessment — built from the template's docs,
 *              lazily paged, filtered by the report's and the widget's filters.
 * Not started  children with none — MAD's feed minus the assessed.
 *
 * Each view owns its own queries and mounts only while it is the one showing,
 * so switching never loads what is not being looked at.
 */
const DATASET_OPTIONS = Object.entries(DATASETS).map(([value, d]) => ({ value, display: d.label }));

const VIEW_OPTIONS = [
  { value: "started", display: "Started" },
  { value: "notStarted", display: "Not started" },
];

const Heading = ({ children }) => (
  <UI_BODY_SEMIBOLD_SM style={{ color: colors2.grey80, display: "block", marginBottom: "0.8rem" }}>{children}</UI_BODY_SEMIBOLD_SM>
);

export const Component = ({ id, widgetIdx, activeFilters = [], presetActiveFilters = [], widgetFilters = [] }) => {
  const [contentType, setContentType] = useState(DATASET_OPTIONS[0].value);
  const [view, setView] = useState(VIEW_OPTIONS[0].value);

  // the report's filters plus the widget's own, the way every widget combines them
  const filters = useMemo(
    () => [...activeFilters, ...presetActiveFilters, ...widgetFilters],
    [activeFilters, presetActiveFilters, widgetFilters]
  );

  if (!isMadTenant()) return <WidgetEmptyState display="This widget is not set up for this platform" />;

  const controls = <PillSwitch options={VIEW_OPTIONS} value={view} onChange={setView} />;
  const View = view === "started" ? StartedChildren : NotStartedChildren;

  return (
    // The Priorities widget's shell: a row flex box capped at the widget
    // height, the scroller its one child, everything inside the scroller.
    // No padding of its own — the white tab band and the grey body run to
    // the widget's edges and carry their own.
    <div style={{ maxHeight: `${WIDGET_MAX_HEIGHT}px`, position: "relative", display: "flex" }}>
      <OverlayScrollbar style={{ flex: 1 }} overlay={false}>
        <div style={{ padding: "1.2rem 1.6rem", background: colors2.white, borderTop: `1px solid ${colors2.grey10}`, borderBottom: `1px solid ${colors2.grey10}` }}>
          <TabBarV2
            id={`${id}:template`}
            options={DATASET_OPTIONS}
            value={contentType}
            onChange={(_id, value) => setContentType(value)}
            saveValueAsString
            style={{ backgroundColor: "transparent" }}
          />
        </div>
        <div style={{ padding: "1.6rem", background: colors2.grey2 }}>
          <Heading>Average Assessment Grade</Heading>
          <div style={{ marginBottom: "2rem" }}>
            <AssessmentStatsBar contentType={contentType} filters={filters} />
          </div>
          <Heading>Per Student Assessment Completion Status</Heading>
          {/* keyed so a view starts clean on a new template: its table
              remounts and measures itself in place */}
          <View
            key={`${contentType}:${view}`}
            id={id}
            widgetIdx={widgetIdx}
            contentType={contentType}
            filters={filters}
            controls={controls}
          />
        </div>
      </OverlayScrollbar>
    </div>
  );
};

// MAD's widget, on MAD only — no other tenant sees it in the picker.
export const optionDef = {
  display: "MAD - Assessment Completion",
  icon: "Table",
  desc: "Which assessments each child has had, per template — and which children have had none.",
  isAvailableToAdd: isMadTenant,
};

export const defaultConfig = { props: {} };

// The templates this widget reads, for the report's filter sidebar (see the
// container's processRawConfigFn).
export const processRawConfigFn = (widgetConfig) => ({
  ...widgetConfig,
  props: { ...widgetConfig.props, contentTypes: Object.keys(DATASETS) },
});
